import { B2BChannelType } from '@tcr-erp/shared';
import type { TenantOrder } from '@/mocks/orders';

/**
 * TCR-2506 — đơn SỈ của khách tổ chức theo 2 kênh B2B "GT" (giá trị) và
 * "MT" (mua theo). Hai kênh này dùng MẪU "ĐƠN HÀNG" riêng (khung Word khách
 * đưa), khác đơn bán lẻ (POS/online) vốn in hoá đơn nhiệt.
 *
 * Kênh "Sỉ" (`B2BChannelType.SI`) KHÔNG thuộc nhóm này — đó là kênh sỉ nội
 * bộ của TCR, vẫn in theo mẫu hoá đơn nhiệt như đơn thường.
 *
 * `b2b_channel` là SNAPSHOT kênh B2B trên đơn (`Order.b2b_channel`, copy từ
 * `Customer.b2b_channel` lúc tạo đơn) — xem `src/mocks/orders.ts`.
 */
export function isWholesaleB2BOrder(
  order: Pick<TenantOrder, 'b2b_channel'> | null | undefined,
): boolean {
  const channel = order?.b2b_channel;
  return channel === B2BChannelType.GT || channel === B2BChannelType.MT;
}

/**
 * Nhóm đơn B2B được nhập "Ghi chú vận chuyển" ở màn sửa đơn khi KHÔNG giao qua
 * ĐVVC (tự vận chuyển/pickup/vault): GT, MT VÀ kênh Sỉ (`SI`, tức đơn sỉ online).
 *
 * Tách khỏi `isWholesaleB2BOrder` vì hàm đó quyết định MẪU IN "ĐƠN HÀNG" (chỉ
 * GT/MT, kênh Sỉ vẫn in hoá đơn nhiệt) — mở ô ghi chú cho Sỉ không được kéo
 * theo đổi mẫu in.
 */
export function canEditShippingNoteB2BOrder(
  order: Pick<TenantOrder, 'b2b_channel'> | null | undefined,
): boolean {
  return isWholesaleB2BOrder(order) || order?.b2b_channel === B2BChannelType.SI;
}

/** 2 chữ số, có số 0 ở đầu — theo giờ máy (local time). */
function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * TCR-2506 — làm sạch MÃ ĐƠN để ghép vào tên file.
 *
 * Windows cấm `\ / : * ? " < > |`, các ký tự điều khiển cũng làm tên file
 * tải xuống bị lỗi/đổi tên. Thay mỗi ký tự cấm bằng `-`, trim khoảng trắng
 * thừa; rỗng thì dùng `DonHang`.
 *
 * CHỈ áp dụng cho TÊN FILE — nội dung tài liệu vẫn dùng mã gốc.
 */
const FORBIDDEN_FILE_NAME_CHARS = new Set(['\\', '/', ':', '*', '?', '"', '<', '>', '|']);

/** Mã đơn rỗng sau khi làm sạch → tên file vẫn phải có phần mã. */
const FALLBACK_FILE_NAME_CODE = 'DonHang';

/** Ký tự điều khiển (0x00–0x1F và 0x7F) — cũng làm tên file lỗi. */
function isControlChar(char: string): boolean {
  const code = char.charCodeAt(0);
  return code <= 0x1f || code === 0x7f;
}

/**
 * Làm sạch mã đơn để đưa vào tên file (xem `buildWholesaleDocxFileName`).
 */
export function sanitizeOrderNumberForFileName(orderNumber: string): string {
  const cleaned = Array.from(orderNumber)
    .map((char) => (isControlChar(char) || FORBIDDEN_FILE_NAME_CHARS.has(char) ? '-' : char))
    .join('')
    .trim();
  return cleaned || FALLBACK_FILE_NAME_CODE;
}

/**
 * TCR-2506 — tên file Word của đơn sỉ theo đúng mẫu khách dùng:
 * `CRM_Đơn hàng_dd.MM.yyyy_HH.mm.ss_<mã đơn>.docx`.
 *
 * Hàm THUẦN — `now` truyền từ ngoài — để test không phụ thuộc đồng hồ hệ
 * thống (và để tên file luôn theo GIỜ ĐỊA PHƯƠNG của máy đang in).
 *
 * Mã đơn được LÀM SẠCH trước khi ghép (ký tự cấm trong tên file → `-`);
 * nội dung tài liệu vẫn dùng mã gốc.
 */
export function buildWholesaleDocxFileName(orderNumber: string, now: Date): string {
  const date = `${pad2(now.getDate())}.${pad2(now.getMonth() + 1)}.${now.getFullYear()}`;
  const time = `${pad2(now.getHours())}.${pad2(now.getMinutes())}.${pad2(now.getSeconds())}`;
  return `CRM_Đơn hàng_${date}_${time}_${sanitizeOrderNumberForFileName(orderNumber)}.docx`;
}
